import {useCallback, useEffect, useState} from 'react';

const STORAGE_KEY = 'little-walks:location-enabled';

/** Geolocation permission state reported by the Permissions API. */
type PermissionState = 'denied' | 'granted' | 'prompt' | 'unknown';

/** Return type of the `useLocationSetting` hook. */
interface UseLocationSettingResult {
    /** Whether location tracking is enabled by the user. */
    isEnabled: boolean;
    /** Browser-level geolocation permission state. */
    permissionState: PermissionState;
    /** Toggles location tracking on or off and persists the choice. */
    setEnabled(value: boolean): void;
}

/**
 * Reads the persisted location setting from `localStorage`.
 *
 * @returns `true` if the user explicitly enabled location, `false` otherwise.
 */
const readSetting = (): boolean => {
    if (typeof window === 'undefined') return false;

    return localStorage.getItem(STORAGE_KEY) === 'true';
};

/**
 * Custom hook that manages the user's location-sharing preference.
 * Persists the choice to `localStorage` and also queries the browser's
 * Permissions API to report whether geolocation is allowed, denied, or pending.
 *
 * @returns An object containing `isEnabled`, `setEnabled`, and `permissionState`.
 */
export const useLocationSetting = (): UseLocationSettingResult => {
    // Startet mit `false` statt direkt `readSetting()` aufzurufen: `localStorage` existiert beim
    // serverseitigen Rendern nicht, würde dort also ohnehin `false` liefern, während der erste
    // Client-Render (noch vor jedem Effekt, also während der Hydration) den echten Wert läse – ein
    // Server/Client-Textunterschied, den React als Hydration-Fehler meldet. Der echte Wert wird
    // stattdessen unten in einem Effekt nachgeladen, der garantiert nur clientseitig läuft.
    const [isEnabled, setIsEnabled] = useState(false);
    const [permissionState, setPermissionState] = useState<PermissionState>('unknown');

    useEffect(() => {
        queueMicrotask(() => {
            if (readSetting()) setIsEnabled(true);
        });
    }, []);

    /**
     * Persists the location preference and updates state.
     *
     * @param value Whether to enable or disable location sharing.
     */
    const setEnabled = useCallback((value: boolean) => {
        localStorage.setItem(STORAGE_KEY, String(value));
        setIsEnabled(value);
    }, []);

    /* Query the Permissions API for geolocation status. */
    useEffect(() => {
        // Not all browsers implement the Permissions API even though `lib.dom.d.ts` declares
        // `navigator.permissions` as always present — cast it to check for real-world absence.
        if (!(navigator.permissions as Permissions | undefined)) return undefined;

        let status: PermissionStatus | null = null;

        /**
         * Handler for permission status changes.
         */
        const handleChange = () => {
            if (status) {
                setPermissionState(status.state as PermissionState);
            }
        };

        /**
         * Queries the current geolocation permission state and subscribes to future changes.
         */
        const queryPermission = async () => {
            const result = await navigator.permissions.query({name: 'geolocation'});

            status = result;
            setPermissionState(result.state as PermissionState);
            result.addEventListener('change', handleChange);
        };

        void queryPermission();

        return () => {
            if (status) {
                status.removeEventListener('change', handleChange);
            }
        };
    }, []);

    return {
        isEnabled,
        permissionState,
        setEnabled
    };
};